import { zodResolver } from '@hookform/resolvers/zod';
import { T, useT } from '@transifex/react';
import { Text, VStack } from '@vidahealth/vida-ui';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { View } from 'react-native';

import { trackEvent } from '@/modules/analytics';
import { updateCustomerProfile } from '@/modules/api';
import { useRulesEngine } from '@/modules/rulesEngine';
import {
  CtaButton,
  Screen,
  ScreenHeading,
  SelectField,
  TextField,
  formatInteger,
} from '@/modules/ui';

import { ETHNICITY_OPTIONS, FEET_OPTIONS, GENDER_OPTIONS, INCHES_OPTIONS } from './constants';
import { createProfileSchema, type CreateProfileFormValues } from './schema/createProfileSchema';
import { buildUpdateProfilePayload } from './utils/buildUpdateProfilePayload';
import { mapDraftToFormValues } from './utils/mapDraftToFormValues';
import { mapFormValuesToDraft } from './utils/mapFormValuesToDraft';

// Caps the weight input at 4 digits (max accepted weight is 1000 lbs).
const WEIGHT_MAX_LENGTH = 4;

/**
 * Collects the remaining profile fields (height, weight, gender, ethnicity)
 * once the user is eligible. Mirrors stylos's CreateProfilePage. On submit it
 * PATCHes the customer profile and calls refreshUser — the rules engine then
 * sees a complete profile (ethnicity + height) and routes forward to
 * /step/eligible, the same "advance via refreshUser" pattern as account
 * creation.
 */
export function CreateProfileScreen() {
  const t = useT();
  const { draft, user, refreshUser, setDraft } = useRulesEngine();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const formDefaultValues = useMemo(() => mapDraftToFormValues(draft), [draft]);

  const { control, handleSubmit } = useForm<CreateProfileFormValues>({
    resolver: zodResolver(createProfileSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: formDefaultValues,
  });

  const onContinue = handleSubmit(async (values) => {
    trackEvent('create-profile-click-continue', { uuid: user?.uuid });
    setSubmitError(false);
    setDraft((prev) => ({ ...prev, ...mapFormValuesToDraft(values) }));

    const customerProfileUri = user?.customerProfile?.resourceUri;
    if (!customerProfileUri) {
      setSubmitError(true);
      return;
    }

    try {
      setIsSubmitting(true);
      await updateCustomerProfile(buildUpdateProfilePayload(customerProfileUri, values));
      // refreshUser flips the freshly-PATCHed profile into state and recomputes
      // currentRoute — now profile-complete, so the engine advances to /eligible.
      await refreshUser();
    } catch {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  });

  return (
    <Screen
      footer={
        <CtaButton
          isBusy={isSubmitting}
          isDisabled={isSubmitting}
          onPress={onContinue}
          testID='create-profile-continue'
        >
          <T _str='Continue' />
        </CtaButton>
      }
    >
      <VStack space='2xl'>
        <ScreenHeading
          title={t('Tell us a little more to continue')}
          subtitle={t('This information will help us find the right health program for you.')}
        />

        {submitError ? (
          <Text size='sm' className='text-error-700'>
            <T _str='Something went wrong saving your profile. Please try again.' />
          </Text>
        ) : null}

        <View className='w-full flex-col gap-6 sm:flex-row'>
          <View className='flex-1'>
            <SelectField
              control={control}
              name='feet'
              options={FEET_OPTIONS}
              placeholder={t('Height (ft)')}
              testID='create-profile-height-ft'
            />
          </View>
          <View className='flex-1'>
            <SelectField
              control={control}
              name='inches'
              options={INCHES_OPTIONS}
              placeholder={t('Height (in)')}
              testID='create-profile-height-in'
            />
          </View>
        </View>

        <TextField
          control={control}
          name='weight'
          placeholder={t('Weight (lbs)')}
          inputMode='numeric'
          keyboardType='number-pad'
          maxLength={WEIGHT_MAX_LENGTH}
          format={formatInteger}
          testID='create-profile-weight'
        />

        <SelectField
          control={control}
          name='gender'
          options={GENDER_OPTIONS}
          placeholder={t('Gender')}
          testID='create-profile-gender'
        />

        <SelectField
          control={control}
          name='ethnicity'
          options={ETHNICITY_OPTIONS}
          placeholder={t('Ethnicity')}
          testID='create-profile-ethnicity'
        />
      </VStack>
    </Screen>
  );
}
